import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap, shareReplay } from 'rxjs/operators';
import { JsonConvert, ValueCheckingMode } from 'json2typescript';
import { environment } from 'src/environments/environment';
import { User } from 'src/app/shared/models/users/user';
import { UserStateService } from '../user/user-state.service';
import { Router } from '@angular/router';
import { LoginUser } from 'src/app/shared/models/users/signupUser';
import { Subject, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private apiRoot = environment.backend_host + '/api/auth/';
  private _user: User;
  private platformId = null;
  private _jsonConvert = new JsonConvert();
  private _userSubject = new Subject<User>();

  constructor(
    private http: HttpClient,
    public router: Router,
    private userState: UserStateService,
    @Inject(PLATFORM_ID) platformId: string
  ) {
    this.platformId = platformId;
    this._jsonConvert.valueCheckingMode = ValueCheckingMode.ALLOW_NULL;
  }

  private setSession(authResult) {
    const tokens = authResult.tokens;
    const accessExpiresAt = JSON.stringify((tokens.access_exp) + new Date().getTime());
    this.userState.setItem('token', tokens.access);
    this.userState.setItem('refresh', tokens.refresh);
    this.userState.setItem('expires_at', accessExpiresAt);
    this.userState.setItem('staffRole', authResult.staff);
    this.setUser(authResult.user);
  }

  private updateTokens(tokens) {
    const accessExpiresAt = JSON.stringify((tokens.access_exp) + new Date().getTime());
    this.userState.setItem('token', tokens.access);
    this.userState.setItem('expires_at', accessExpiresAt);
  }

  private setUser(user: User) {
    this.userState.setItem('user', JSON.stringify(user));
    this._user = this._jsonConvert.deserializeObject(user, User);
    this._userSubject.next(this._user);
  }

  public get user(): User {
    if (this._user) {
      return this._user;
    }
    const json = this.userState.getItem('user');
    try {
      const user = this._jsonConvert.deserializeObject(JSON.parse(json), User);
      return user;
    } catch (e) {
      return null;
    }
  }

  public getToken(): string {
    if (this.isAuthenticated()) {
      return this.userState.getItem('token');
    } else {
      return '';
    }
  }

  public getRefreshToken(): string {
    return this.userState.getItem('refresh');
  }

  login(userData: LoginUser) {
    return this.http.post(
      this.apiRoot.concat('login'),
      {
        username: userData.username,
        password: userData.password,
      }
    ).pipe(
      tap(response => this.setSession(response)),
      shareReplay(),
    );
  }

  public logout(redirect: boolean = true): void {
    // Remove tokens and expiry time from cookies
    this.userState.removeItem('token');
    this.userState.removeItem('refresh');
    this.userState.removeItem('expires_at');
    this.userState.removeItem('user');
    this._user = null;

    if (redirect === true) {
      // Go back to the home route
      this.router.navigate(['/login']);
    }
  }

  public resetPassword(username) {
    return this.http.post(
      this.apiRoot.concat('reset_password'),
      {
        username: username,
      }
    ).pipe(
      shareReplay(),
    );
  }

  public isAuthenticated(): boolean {
    // Check whether the current time is past the
    // Access Token's expiry time
    const expiresAt = JSON.parse(this.userState.getItem('expires_at') || '{}');
    return new Date().getTime() < expiresAt;
  }

  refreshToken() {
    return this.http.post(
      this.apiRoot.concat('token/refresh'),
      { refresh: this.getRefreshToken() }
    ).subscribe(response => {
      this.updateTokens(response);
    });
  }

  public set loginRedirectUrl(redirectUrl: string) {
    if (!redirectUrl) {
      this.userState.removeItem('loginRedirectUrl');
    } else {
      this.userState.setItem('loginRedirectUrl', redirectUrl);
    }
  }

  public get loginRedirectUrl(): string {
    return this.userState.getItem('loginRedirectUrl') || '/';
  }

  public get userSubject(): Subject<User> {
    return this._userSubject;
  }
}
